import React, { useState } from 'react';
import {
  Container, RowView, Col, Navbar, Heading, Paragraph, Text,
  Button, CardView, Image, FormView, Input, Spacer, Divider,
  Alert, Badge, ListView, ListItem, Flex, Grid, Section,
  Modal, Accordion, Tabs, Dropdown, Carousel, Progress,
  Toast, Offcanvas, Pagination, Spinner, Breadcrumb, Link, Table
} from './index';

const ComponentDemo = () => {
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  return (
    <>
      {/* Navbar Example */}
      <Navbar brand="Component Demo" bg="dark" variant="dark" expand="lg" />

      <Container fluid={false}>
        <Spacer height="30px" />

        {/* Breadcrumb Example */}
        <Breadcrumb 
          items={[
            { label: 'Home', href: '/' },
            { label: 'Components', href: '/components' },
            { label: 'Demo', active: true }
          ]}
        />

        <Spacer height="20px" />

        {/* Header Section */}
        <Section id="header">
          <Heading level={1}>Component Library Demo</Heading>
          <Paragraph className="lead">
            Explore all available components for easy page building.
          </Paragraph>
        </Section>

        <Divider />

        {/* Alert Examples */}
        <Section id="alerts">
          <Heading level={2}>Alerts</Heading>
          <Alert variant="success" dismissible heading="Success!">
            Your operation was successful.
          </Alert>
          <Alert variant="warning">
            This is a warning message.
          </Alert>
          <Alert variant="info">
            This is an informational message.
          </Alert>
        </Section>

        <Spacer height="30px" />

        {/* Layout with Cards */}
        <Section id="cards">
          <Heading level={2}>Cards & Layout</Heading>
          <RowView xs={1} md={2} lg={3}>
            <Col>
              <CardView 
                title="Card 1" 
                subtitle="With subtitle"
                footer="Card footer"
              >
                <Paragraph>This is a card with a title, subtitle, and footer.</Paragraph>
                <Button variant="primary" size="sm">Action</Button>
              </CardView>
            </Col>
            <Col>
              <CardView 
                title="Card 2" 
                bg="light"
                header="Card Header"
              >
                <Paragraph>This card has a header and light background.</Paragraph>
                <Badge bg="success" pill>New</Badge>
              </CardView>
            </Col>
            <Col>
              <CardView title="Card 3" border="primary">
                <Image 
                  src="https://via.placeholder.com/300x200" 
                  alt="Placeholder" 
                  fluid 
                  rounded
                />
                <Spacer height="10px" />
                <Paragraph>Card with an image.</Paragraph>
              </CardView>
            </Col>
          </RowView>
        </Section>

        <Spacer height="30px" />

        {/* Flex Layout Example */}
        <Section id="flex">
          <Heading level={2}>Flex Layout</Heading>
          <Flex justify="space-between" align="center" gap="10px">
            <Button variant="primary">Button 1</Button>
            <Button variant="secondary">Button 2</Button>
            <Button variant="success">Button 3</Button>
            <Button variant="danger">Button 4</Button>
          </Flex>
        </Section>

        <Spacer height="30px" />

        {/* Grid Layout Example */}
        <Section id="grid">
          <Heading level={2}>Grid Layout</Heading>
          <Grid columns="repeat(auto-fit, minmax(150px, 1fr))" gap="15px">
            <CardView bg="primary" text="white">
              <Paragraph>Grid Item 1</Paragraph>
            </CardView>
            <CardView bg="secondary" text="white">
              <Paragraph>Grid Item 2</Paragraph>
            </CardView>
            <CardView bg="success" text="white">
              <Paragraph>Grid Item 3</Paragraph>
            </CardView>
            <CardView bg="danger" text="white">
              <Paragraph>Grid Item 4</Paragraph>
            </CardView>
            <CardView bg="warning">
              <Paragraph>Grid Item 5</Paragraph>
            </CardView>
            <CardView bg="info" text="white">
              <Paragraph>Grid Item 6</Paragraph>
            </CardView>
          </Grid>
        </Section>

        <Spacer height="30px" />

        {/* Buttons & Badges */}
        <Section id="buttons">
          <Heading level={2}>Buttons & Badges</Heading>
          <Flex gap="10px" wrap="wrap">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="success">Success</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="warning">Warning</Button>
            <Button variant="info">Info</Button>
            <Button variant="light">Light</Button>
            <Button variant="dark">Dark</Button>
          </Flex>
          
          <Spacer height="15px" />
          
          <Flex gap="10px" wrap="wrap">
            <Badge bg="primary">Primary</Badge>
            <Badge bg="secondary">Secondary</Badge>
            <Badge bg="success" pill>Success Pill</Badge>
            <Badge bg="danger" pill>Danger Pill</Badge>
            <Badge bg="warning">Warning</Badge>
            <Badge bg="info">Info</Badge>
          </Flex>
        </Section>

        <Spacer height="30px" />

        {/* Form Example */}
        <Section id="forms">
          <Heading level={2}>Forms</Heading>
          <RowView>
            <Col md={6}>
              <FormView>
                <Input 
                  label="Full Name"
                  type="text"
                  placeholder="Enter your name"
                  required
                />
                <Input 
                  label="Email"
                  type="email"
                  placeholder="Enter your email"
                  helpText="We'll never share your email"
                  required
                />
                <Input 
                  label="Password"
                  type="password"
                  placeholder="Enter password"
                  required
                />
                <Input 
                  label="Comments"
                  as="textarea"
                  rows={3}
                  placeholder="Your comments"
                />
                <Button type="submit" variant="primary">Submit</Button>
              </FormView>
            </Col>
          </RowView>
        </Section>

        <Spacer height="30px" />

        {/* List Example */}
        <Section id="lists">
          <Heading level={2}>Lists</Heading>
          <RowView>
            <Col md={4}>
              <ListView>
                <ListItem>Default Item 1</ListItem>
                <ListItem active>Active Item</ListItem>
                <ListItem>Default Item 3</ListItem>
              </ListView>
            </Col>
            <Col md={4}>
              <ListView variant="flush" numbered>
                <ListItem>Numbered Item 1</ListItem>
                <ListItem>Numbered Item 2</ListItem>
                <ListItem>Numbered Item 3</ListItem>
              </ListView>
            </Col>
            <Col md={4}>
              <ListView>
                <ListItem action variant="primary">
                  <Flex justify="space-between">
                    <span>Action Item</span>
                    <Badge bg="light" text="dark">4</Badge>
                  </Flex>
                </ListItem>
                <ListItem action variant="success">
                  <Flex justify="space-between">
                    <span>Success Item</span>
                    <Badge bg="light" text="dark">2</Badge>
                  </Flex>
                </ListItem>
              </ListView>
            </Col>
          </RowView>
        </Section>

        <Spacer height="30px" />

        {/* Interactive Components */}
        <Section id="interactive">
          <Heading level={2}>Interactive Components</Heading>
          <Flex gap="10px" wrap="wrap">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              Open Modal
            </Button>
            <Button variant="success" onClick={() => setShowToast(true)}>
              Show Toast
            </Button>
            <Button variant="info" onClick={() => setShowOffcanvas(true)}>
              Open Offcanvas
            </Button>
            <Dropdown 
              title="Dropdown Menu"
              variant="warning"
              items={[
                { label: 'Action 1', onClick: () => alert('Action 1') },
                { label: 'Action 2', onClick: () => alert('Action 2') },
                { divider: true },
                { label: 'Separated Action', onClick: () => alert('Separated') }
              ]}
            />
          </Flex>
        </Section>

        <Spacer height="30px" />

        {/* Accordion Example */}
        <Section id="accordion">
          <Heading level={2}>Accordion</Heading>
          <Accordion 
            items={[
              {
                header: 'Accordion Item 1',
                body: 'This is the content for the first accordion item. You can put any content here.'
              },
              {
                header: 'Accordion Item 2',
                body: 'This is the content for the second accordion item.'
              },
              {
                header: 'Accordion Item 3',
                body: 'This is the content for the third accordion item.'
              }
            ]}
          />
        </Section>

        <Spacer height="30px" />

        {/* Tabs Example */}
        <Section id="tabs">
          <Heading level={2}>Tabs</Heading>
          <Tabs 
            items={[
              {
                title: 'Home',
                content: (
                  <div style={{ padding: '20px' }}>
                    <Heading level={3}>Home Tab</Heading>
                    <Paragraph>This is the content of the Home tab.</Paragraph>
                  </div>
                )
              },
              {
                title: 'Profile',
                content: (
                  <div style={{ padding: '20px' }}>
                    <Heading level={3}>Profile Tab</Heading>
                    <Paragraph>This is the content of the Profile tab.</Paragraph>
                  </div>
                )
              },
              {
                title: 'Settings',
                content: (
                  <div style={{ padding: '20px' }}>
                    <Heading level={3}>Settings Tab</Heading>
                    <Paragraph>This is the content of the Settings tab.</Paragraph>
                  </div>
                )
              }
            ]}
          />
        </Section>

        <Spacer height="30px" />

        {/* Table Example */}
        <Section id="table">
          <Heading level={2}>Table</Heading>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>John Doe</td>
                <td>john@example.com</td>
                <td><Badge bg="success">Active</Badge></td>
              </tr>
              <tr>
                <td>2</td>
                <td>Jane Smith</td>
                <td>jane@example.com</td>
                <td><Badge bg="warning">Pending</Badge></td>
              </tr>
              <tr>
                <td>3</td>
                <td>Bob Johnson</td>
                <td>bob@example.com</td>
                <td><Badge bg="danger">Inactive</Badge></td>
              </tr>
            </tbody>
          </Table>
        </Section>

        <Spacer height="30px" />

        {/* Progress Bars */}
        <Section id="progress">
          <Heading level={2}>Progress Bars</Heading>
          <Progress now={25} label="25%" variant="primary" />
          <Spacer height="10px" />
          <Progress now={50} variant="success" striped />
          <Spacer height="10px" />
          <Progress now={75} variant="warning" striped animated />
          <Spacer height="10px" />
          <Progress now={100} label="Complete!" variant="danger" />
        </Section>

        <Spacer height="30px" />

        {/* Carousel Example */}
        <Section id="carousel">
          <Heading level={2}>Carousel</Heading>
          <Carousel 
            items={[
              {
                image: 'https://via.placeholder.com/800x400/007bff/ffffff?text=Slide+1',
                title: 'First Slide',
                description: 'This is the first slide'
              },
              {
                image: 'https://via.placeholder.com/800x400/28a745/ffffff?text=Slide+2',
                title: 'Second Slide',
                description: 'This is the second slide'
              },
              {
                image: 'https://via.placeholder.com/800x400/dc3545/ffffff?text=Slide+3',
                title: 'Third Slide',
                description: 'This is the third slide'
              }
            ]}
            interval={3000}
          />
        </Section>

        <Spacer height="30px" />

        {/* Pagination Example */}
        <Section id="pagination">
          <Heading level={2}>Pagination</Heading>
          <Flex justify="center">
            <Pagination 
              totalPages={10}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </Flex>
          <Paragraph style={{ textAlign: 'center' }}>
            Current Page: <Badge bg="primary">{currentPage}</Badge>
          </Paragraph>
        </Section>

        <Spacer height="30px" />

        {/* Spinner Example */}
        <Section id="spinner">
          <Heading level={2}>Spinners</Heading>
          <Flex gap="20px">
            <Spinner animation="border" variant="primary" />
            <Spinner animation="border" variant="success" />
            <Spinner animation="grow" variant="danger" />
            <Spinner animation="grow" variant="warning" />
            <Spinner animation="border" variant="info" size="sm" />
          </Flex>
        </Section>

        <Spacer height="30px" />

        {/* Utility Components */}
        <Section id="utilities">
          <Heading level={2}>Utility Components</Heading>
          <Text tag="div">
            <strong>Spacer:</strong> Adds consistent spacing
          </Text>
          <Divider color="#007bff" thickness="2px" />
          <Text tag="div">
            <strong>Divider:</strong> Visual separator between sections
          </Text>
          <Divider />
          <Text tag="div">
            <strong>Link:</strong> <Link to="/">Internal Link</Link> | <Link href="https://example.com" external>External Link</Link>
          </Text>
        </Section>

        <Spacer height="50px" />

      </Container>

      {/* Modal Component */}
      <Modal 
        show={showModal}
        onHide={() => setShowModal(false)}
        title="Example Modal"
        size="lg"
        centered
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              Save Changes
            </Button>
          </>
        }
      >
        <Paragraph>
          This is an example modal dialog. You can put any content here including forms, images, and more.
        </Paragraph>
        <Alert variant="info">
          Modals are great for focused user interactions!
        </Alert>
      </Modal>

      {/* Toast Component */}
      <Toast 
        show={showToast}
        onHide={() => setShowToast(false)}
        title="Notification"
        delay={3000}
        autohide
        bg="success"
        position="top-end"
      >
        This is a toast notification!
      </Toast>

      {/* Offcanvas Component */}
      <Offcanvas 
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        title="Offcanvas Menu"
        placement="start"
      >
        <Heading level={4}>Menu</Heading>
        <ListView variant="flush">
          <ListItem action>Dashboard</ListItem>
          <ListItem action>Profile</ListItem>
          <ListItem action>Settings</ListItem>
          <ListItem action>Help</ListItem>
        </ListView>
        <Spacer height="20px" />
        <Button variant="primary" block onClick={() => setShowOffcanvas(false)}>
          Close Menu
        </Button>
      </Offcanvas>
    </>
  );
};

export default ComponentDemo;

